import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import styled from 'styled-components';

import { getTheoriesByExerciseId } from '../../../../actions/homework';

import Fab from '../../../floating-action-button';
import ColumnContainer from '../../../layouts/components/column-container';
import { useWindowSize } from '../../../../lib/hooks';
import AccordionTheory from '../../../accordion-theory';
import theme from '../../../../theme';
import TheoryList from '../../../theory-list';
import { showTheoriesModal } from '../../../../actions/modal';

import IconTheory from './assets/theoryHelp.svg?react';

const Container = styled.div`
    display: flex;
    flex-direction: row-reverse;
    position: ${(props) => (props.theoriesOpened ? 'initial' : 'fixed')};
    bottom: 80px;

    ${(props) => props.theme.mediaQueries.mobile} {
        padding-right: 0;
        right: ${(props) => !props.theoriesOpened && `${props.theme.space.mobile}px`};
    }

    ${(props) => props.theme.mediaQueries.tablet} {
        padding-right: ${(props) => !props.theoriesOpened && `${props.theme.space.small}px`};
        right: 0;
    }

    ${(props) => props.theme.mediaQueries.desktop} {
        padding-right: ${(props) => !props.theoriesOpened && `${props.theme.space.mobile}px`};
        right: 0;
    }
`;

const TheoryContainer = styled.div`
    ${(props) => props.theme.mediaQueries.mobile} {
        margin-top: ${(props) => `${props.theme.space.desktop}px`};
    }

    ${(props) => props.theme.mediaQueries.tablet} {
        margin-top: ${(props) => `${props.theme.space.large}px`};
    }

    ${(props) => props.theme.mediaQueries.desktop} {
        margin-top: ${(props) => `${props.theme.space.large}px`};
    }

    & > div:not(:last-child) {
        margin-bottom: ${(props) => `${props.theme.space.small}px`};
    }
`;

export const ActionsBar = ({
    exercise,
    showSuggestions,
    theories,
    theoriesStatus,
    getTheoriesByExerciseId,
    bookId,
    loadTheories,
    showTheoriesModal,
}) => {
    const [theoriesCount, setTheoriesCount] = useState(0);
    const [theoriesOpened, setTheoriesOpened] = useState(false);
    const [filteredTheories, setFilteredTheories] = useState([]);
    const windowSize = useWindowSize();
    const ref = useRef();

    const scrollToRef = (ref) => window.scrollTo({ behavior: 'smooth', top: ref.current.offsetTop });

    useEffect(() => {
        const exerciseTheories = theories.find((theory) => exercise.id && theory.id === exercise.id);

        if (exerciseTheories) {
            const sum = Object.keys(exerciseTheories.data).reduce((acc, cur) => exerciseTheories.data[cur].length + acc, 0);

            setTheoriesCount(sum);
            setFilteredTheories(exerciseTheories.data);
        }

        if (loadTheories && showSuggestions && exercise.id && !exerciseTheories && !theoriesStatus.fetching) {
            getTheoriesByExerciseId(exercise.id, bookId);
        }
        return () => {};
    }, [
        loadTheories,
        showSuggestions,
        exercise.id,
        theories,
        theoriesStatus.fetching,
        bookId,
        getTheoriesByExerciseId,
        setTheoriesCount,
        setFilteredTheories,
    ]);

    const handleClickHelp = (checked) => {
        setTheoriesOpened(checked);
        if (checked) {
            // This delay because the position of button change, we need to wait a bit
            setTimeout(() => {
                scrollToRef(ref);
            }, 100);
        }
    };

    return (
        <div>
            <Container ref={ref} theoriesOpened={theoriesOpened}>
                {!theoriesOpened && <ColumnContainer width={[0, 0, 1 / 12]} />}
                {theoriesCount > 0 && (
                    <Fab
                        variant="helper"
                        checkable={true}
                        text={`Aiuti di teoria (${theoriesCount})`}
                        resizableOnScroll={windowSize.innerWidth < theme.breakpoints.tablet}
                        onClick={(e, checked) => handleClickHelp(checked)}
                    >
                        <IconTheory />
                    </Fab>
                )}
            </Container>
            {theoriesOpened && (
                <TheoryContainer>
                    {Object.keys(filteredTheories).map((value) => {
                        const t = filteredTheories[value];
                        return (
                            <AccordionTheory
                                key={`${t[0].theme.name}${t[0].microTheme.name}`}
                                theme={t[0].theme.name}
                                microTheme={`${t[0].microTheme.name} (${t.length})`}
                                showTheoriesModal={() => showTheoriesModal(value, t[0].theme.name, t[0].microTheme.name, t)}
                            >
                                <TheoryList microThemeId={value} theories={t} />
                            </AccordionTheory>
                        );
                    })}
                </TheoryContainer>
            )}
        </div>
    );
};

ActionsBar.propTypes = {
    theories: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string,
            data: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
        }),
    ),
    theoriesStatus: PropTypes.shape({
        fetching: PropTypes.bool,
        loaded: PropTypes.bool,
        error: PropTypes.bool,
    }),
    bookId: PropTypes.number,
    correctionsStatus: PropTypes.array,
    exercise: PropTypes.any,
    getTheoriesByExerciseId: PropTypes.func,
    homeworkTerminated: PropTypes.bool,
    loadTheories: PropTypes.bool,
    onCorrection: PropTypes.func,
    profile: PropTypes.object,
    showSuggestions: PropTypes.bool,
    showTheoriesModal: PropTypes.func,
};

const mapStateToProps = (state) => {
    return {
        bookId: state.application.bookId,
        correctionsStatus: state.status.correctionsStatus,
        loadTheories: state.application.loadTheories,
        showSuggestions: state.application.showSuggestions,
        theories: state.theory.theories,
        theoriesStatus: state.theory.status,
    };
};

const actionCreators = {
    getTheoriesByExerciseId,
    showTheoriesModal,
};

export default connect(mapStateToProps, actionCreators)(ActionsBar);
